import React, { useEffect, useMemo, useState } from 'react';
import { downloadFile } from '../../lib/assets';
import { PERIOD_LABEL, shortDay } from '../../lib/licences';
import { contractsApi, errorOf, STATUS_LABEL, DOC_LABEL, capWords, rateWords } from '../../lib/contracts';
import '../licences/Licences.css';
import './Contracts.css';

/**
 * THE CONTRACTOR'S OWN CONTRACT, in the portal (120), read-only - agreed
 * with Gordon: the dates, the notice period and the deadline, the rate in
 * force today and the rate card, the caps and how much is used, and the
 * documents NCMB has shared. Never the internal notes (the server leaves
 * them out). At the top of the Billing tab, where the rate matters; drawn
 * only when there is a contract to show, so a contractor without one sees
 * the tab as before.
 */
export default function MyContract({ contractorId, token }) {
  const auth = useMemo(() => (token ? { headers: { Authorization: `Bearer ${token}` } } : undefined), [token]);
  const [data, setData] = useState(null);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    if (!contractorId) return undefined;
    let live = true;
    contractsApi.mine(contractorId, auth).then((d) => { if (live) setData(d); }).catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [contractorId, auth]);

  const contracts = data?.contracts || [];
  if (!contracts.length) return null;

  const get = (k, d) => downloadFile(contractsApi.myDocumentPath(contractorId, k.id, d.id), d.file_name)
    .catch((e) => setProblem(errorOf(e, 'The file could not be downloaded.')));

  return (
    <section className="con-mine" aria-label="Your contract" data-testid="my-contract">
      <h2 className="con-mine-title">{contracts.length === 1 ? 'Your contract' : 'Your contracts'}</h2>
      {problem && <p className="as-error" role="alert">{problem}</p>}
      {contracts.map((k) => {
        const caps = [['Hours', capWords(k.cap.hours, 'hours', k.currency)], ['Money', capWords(k.cap.amount, 'amount', k.currency)]]
          .filter(([, c]) => c);
        return (
          <div key={k.id} className="as-panel con-card" data-contract={k.id}>
            <div className="lic-panel-head">
              <h3 className="con-card-title">{k.title}</h3>
              {k.status !== 'active' && <span className="as-chip">{STATUS_LABEL[k.status]}</span>}
            </div>
            <dl className="as-facts">
              {k.company_name && <><dt>For</dt><dd>{k.company_name}</dd></>}
              {k.reference && <><dt>Reference</dt><dd>{k.reference}</dd></>}
              <dt>Dates</dt><dd>{k.start_date ? `${shortDay(k.start_date)} – ${k.end_date ? shortDay(k.end_date) : 'until ended'}` : '—'}</dd>
              {k.renewal_date && (
                <><dt>Renews</dt><dd>{`${shortDay(k.renewal_date)}${k.auto_renew ? ' by itself' : ''}${k.billing_period ? `, ${PERIOD_LABEL[k.billing_period].toLowerCase()}` : ''}`}</dd></>
              )}
              {k.notice_days ? <><dt>Notice</dt><dd>{`${k.notice_days} days${k.notice_deadline ? ` - by ${shortDay(k.notice_deadline)}` : ''}`}</dd></> : null}
              <dt>Rate today</dt>
              <dd data-testid="my-rate">
                {rateWords(k.rate_now, k.currency) || 'Your usual rate'}
                {k.category_rates.length > 0 && (
                  <span className="lic-seat-sub">{k.category_rates.map((c) => `${c.category_name}: ${rateWords(c.hourly_rate, k.currency)}`).join(' · ')}</span>
                )}
              </dd>
              {caps.map(([label, c]) => (
                <React.Fragment key={label}>
                  <dt>{`${label} cap`}</dt>
                  <dd><span className="as-chip" data-tone={c.tone}>{c.text}</span></dd>
                </React.Fragment>
              ))}
              {k.scope && <><dt>Scope</dt><dd>{k.scope}</dd></>}
            </dl>
            {k.rates.length > 1 && (
              <details className="con-mine-rates">
                <summary className="as-quiet">The rate card</summary>
                <ul>
                  {k.rates.map((r) => (
                    <li key={`${r.category_name || ''}${r.effective_from}`}>
                      {`${r.category_name || 'Any work'}: ${rateWords(r.hourly_rate, k.currency)} from ${shortDay(r.effective_from)}${r.effective_to ? ` to ${shortDay(r.effective_to)}` : ''}`}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {k.documents.length > 0 && (
              <ul className="lic-docs">
                {k.documents.map((d) => (
                  <li key={d.id}>
                    <button type="button" className="btn btn-plain btn-small" style={{ paddingInline: 0 }} onClick={() => get(k, d)}>{d.file_name}</button>
                    <span className="lic-seat-sub">{DOC_LABEL[d.doc_type] || d.doc_type}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </section>
  );
}
